import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore";

const SESSION_KEY = "gh_sl@g";
const LOGIN_PATH = "/login";

// Pages a logged-out visitor may stay on (no redirect from these).
const PUBLIC_PATHS = ["/login", "/signup", "/register", "/forgetpass"];

const ALLOWED_ORIGINS = [
  "http://songjukti.com",
  "https://songjukti.com",
  "http://user.songjukti.com",
  "https://user.songjukti.com",
];

const decodeSessionUser = () => {
  try {
    const encoded = window.localStorage.getItem(SESSION_KEY);
    if (!encoded) return null;
    return JSON.parse(atob(encoded));
  } catch (error) {
    console.error("Failed to decode stored session:", error);
    return null;
  }
};

// A session is trusted if it has a sopnoid AND either
//  - has no `origin` (written by this app itself), or
//  - carries an `origin` we recognise (written by the SSO login app).
const isTrustedSession = (u) => {
  if (!u?.sopnoid) return false;
  if (!u.origin) return true;
  return [...ALLOWED_ORIGINS, window.location.origin].includes(u.origin);
};

const memberCheck = (u) => !!(u?.refer && u?.sopnoid && u?.membership);

// Source of truth is the zustand store (hydrated from localStorage). If the
// store is empty it tries, in order: the stored session, the SSO cookie from
// the main domain, then redirects to /login. It also reacts to the session
// ending in another tab.
//
// Must be used inside <BrowserRouter>.
export const useSessionUser = ({ redirectIfLoggedOut = true } = {}) => {
  const { user, setUser } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLogged, setIsLogged] = useState(!!user?.sopnoid);
  const [isMember, setIsMember] = useState(memberCheck(user));

  // 1) Keep flags in sync, restore a session, or redirect to login.
  useEffect(() => {
    const validLogin = !!user?.sopnoid;
    setIsLogged(validLogin);
    setIsMember(memberCheck(user));

    if (validLogin) return;

    const storedUser = decodeSessionUser();

    if (isTrustedSession(storedUser)) {
      setUser(storedUser); // effect re-runs with the restored user
      return;
    }

    if (storedUser) {
      console.warn("Untrusted or invalid stored session — clearing it");
      window.localStorage.removeItem(SESSION_KEY);
    }

    // Signed in on the main domain? Adopt that session.
    if (useAuthStore.getState().hydrateFromCookie()) return;

    const onPublicPage = PUBLIC_PATHS.includes(location.pathname.toLowerCase());
    if (redirectIfLoggedOut && !onPublicPage) {
      navigate(LOGIN_PATH, {
        replace: true,
        state: { from: location.pathname + location.search },
      });
    }
  }, [user, setUser, navigate, location.pathname, location.search, redirectIfLoggedOut]);

  // 2) Session ended / changed in another tab ("storage" only fires in OTHER tabs).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== null && e.key !== SESSION_KEY) return; // null = storage.clear()

      const store = useAuthStore.getState();
      const current = store.user;

      if (!e.newValue) {
        store.clearSession(); // another tab logged out -> leave cleanly to /login
        return;
      }

      const next = decodeSessionUser();
      if (isTrustedSession(next) && next.sopnoid !== current?.sopnoid) {
        store.disconnectSocket();
        store.setUser(next);
        useAuthStore.getState().connectSocket();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return { user, isLogged, setIsLogged, isMember };
};

// Mount once inside <BrowserRouter>. Renders nothing; keeps the session
// watcher + redirect-to-login logic alive for the whole app.
export const SessionGuard = () => {
  useSessionUser();
  return null;
};

export default useSessionUser;